import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  TaskFilters,
  SavedFilterSet,
  BoardView,
  GroupByOption,
  TaskListSortBy,
  TimelineZoom,
} from '../types';

const EMPTY_SAVED_FILTER_SETS: SavedFilterSet[] = [];

const EMPTY_FILTERS: TaskFilters = {
  assigneeIds: [],
  statuses: [],
  search: '',
};

/**
 * Personal (per-user) subset of the List view's config — `visibleColumns`/column order/width are
 * a board-level *shared* setting instead (see `useSharedListViewConfig`), not tracked here.
 */
interface TaskListPersistedConfig {
  groupBy: GroupByOption;
  sortBy?: TaskListSortBy;
}

const DEFAULT_LIST_CONFIG: TaskListPersistedConfig = {
  groupBy: 'group',
};

/** Timeline's own *personal* display config — currently just the "Lapso" (zoom) granularity. */
interface TimelinePersistedConfig {
  zoom: TimelineZoom;
}

const DEFAULT_TIMELINE_CONFIG: TimelinePersistedConfig = {
  zoom: 'week',
};

/**
 * Everything scoped to one board (`boardId`) that a user expects to see restored exactly as they
 * left it. `filters`/`savedFilterSets`/`showCompleted`/`showArchived`/`lastActiveView` apply to
 * every view type of the board (list/board/calendar/gantt/…) — the same task set and toggles carry
 * over when switching tabs. `views` is the per-view-type extensibility point: each view type that
 * grows its own *personal* display settings (today only List's grouping/sort) gets its own key
 * here, so adding a new view in the future never requires reshaping this store.
 */
interface BoardViewSettings {
  filters: TaskFilters;
  savedFilterSets: SavedFilterSet[];
  showCompleted: boolean;
  showArchived: boolean;
  lastActiveView?: BoardView;
  views: {
    list: TaskListPersistedConfig;
    timeline: TimelinePersistedConfig;
  };
}

const EMPTY_BOARD_SETTINGS: BoardViewSettings = {
  filters: EMPTY_FILTERS,
  savedFilterSets: EMPTY_SAVED_FILTER_SETS,
  showCompleted: false,
  showArchived: false,
  lastActiveView: undefined,
  views: { list: DEFAULT_LIST_CONFIG, timeline: DEFAULT_TIMELINE_CONFIG },
};

function getBoard(settingsByBoard: Record<string, BoardViewSettings>, boardId: string) {
  return settingsByBoard[boardId] ?? EMPTY_BOARD_SETTINGS;
}

function sanitizeListConfig(value: unknown): TaskListPersistedConfig {
  const parsed = (value ?? {}) as Partial<TaskListPersistedConfig>;
  return {
    groupBy: parsed.groupBy ?? DEFAULT_LIST_CONFIG.groupBy,
    sortBy: parsed.sortBy,
  };
}

const TIMELINE_ZOOMS: readonly TimelineZoom[] = ['7d', '14d', 'day', 'week', 'month'];

function sanitizeTimelineConfig(value: unknown): TimelinePersistedConfig {
  const parsed = (value ?? {}) as Partial<TimelinePersistedConfig>;
  // Persisted state predates this key entirely (added after List's), and an older
  // build stored the Gantt's zoom vocabulary here — reject anything unrecognized.
  return {
    zoom:
      parsed.zoom && TIMELINE_ZOOMS.includes(parsed.zoom)
        ? parsed.zoom
        : DEFAULT_TIMELINE_CONFIG.zoom,
  };
}

interface TaskViewState {
  settingsByBoard: Record<string, BoardViewSettings>;
  setFilters: (boardId: string, filters: TaskFilters) => void;
  resetFilters: (boardId: string) => void;
  setShowCompleted: (boardId: string, show: boolean) => void;
  setShowArchived: (boardId: string, show: boolean) => void;
  setSavedFilterSets: (boardId: string, sets: SavedFilterSet[]) => void;
  setLastActiveView: (boardId: string, view: BoardView) => void;
  setListConfig: (
    boardId: string,
    updater: TaskListPersistedConfig | ((prev: TaskListPersistedConfig) => TaskListPersistedConfig),
  ) => void;
  setTimelineZoom: (boardId: string, zoom: TimelineZoom) => void;
}

function updateBoard(
  settingsByBoard: Record<string, BoardViewSettings>,
  boardId: string,
  patch: Partial<BoardViewSettings>,
): Record<string, BoardViewSettings> {
  return {
    ...settingsByBoard,
    [boardId]: { ...getBoard(settingsByBoard, boardId), ...patch },
  };
}

export const useTaskViewStore = create<TaskViewState>()(
  persist(
    (set) => ({
      settingsByBoard: {},
      setFilters: (boardId, filters) =>
        set((state) => ({
          settingsByBoard: updateBoard(state.settingsByBoard, boardId, { filters }),
        })),
      resetFilters: (boardId) =>
        set((state) => ({
          settingsByBoard: updateBoard(state.settingsByBoard, boardId, { filters: EMPTY_FILTERS }),
        })),
      setShowCompleted: (boardId, show) =>
        set((state) => ({
          settingsByBoard: updateBoard(state.settingsByBoard, boardId, { showCompleted: show }),
        })),
      setShowArchived: (boardId, show) =>
        set((state) => ({
          settingsByBoard: updateBoard(state.settingsByBoard, boardId, { showArchived: show }),
        })),
      setSavedFilterSets: (boardId, sets) =>
        set((state) => ({
          settingsByBoard: updateBoard(state.settingsByBoard, boardId, { savedFilterSets: sets }),
        })),
      setLastActiveView: (boardId, view) =>
        set((state) => ({
          settingsByBoard: updateBoard(state.settingsByBoard, boardId, { lastActiveView: view }),
        })),
      setListConfig: (boardId, updater) =>
        set((state) => {
          const board = getBoard(state.settingsByBoard, boardId);
          const next = typeof updater === 'function' ? updater(board.views.list) : updater;
          return {
            settingsByBoard: updateBoard(state.settingsByBoard, boardId, {
              views: { ...board.views, list: next },
            }),
          };
        }),
      setTimelineZoom: (boardId, zoom) =>
        set((state) => {
          const board = getBoard(state.settingsByBoard, boardId);
          return {
            settingsByBoard: updateBoard(state.settingsByBoard, boardId, {
              views: { ...board.views, timeline: { zoom } },
            }),
          };
        }),
    }),
    {
      name: 'tasks:view-store',
      version: 3,
      partialize: (state) => ({ settingsByBoard: state.settingsByBoard }),
      // Sanitizes `views.list` on every rehydration — persisted state can predate this shape
      // (older versions stored `visibleColumns` here too, before it moved to the board-level
      // shared config) or be hand-edited/corrupted.
      merge: (persisted, current) => {
        const incoming = (persisted as Partial<TaskViewState> | undefined)?.settingsByBoard ?? {};
        const settingsByBoard = Object.fromEntries(
          Object.entries(incoming).map(([boardId, board]) => [
            boardId,
            {
              ...EMPTY_BOARD_SETTINGS,
              ...board,
              views: {
                list: sanitizeListConfig(board?.views?.list),
                timeline: sanitizeTimelineConfig(board?.views?.timeline),
              },
            },
          ]),
        );
        return { ...current, settingsByBoard };
      },
    },
  ),
);

export function useTaskFilters(boardId: string) {
  const { filters, setFilters, resetFilters } = useTaskViewStore(
    useShallow((s) => ({
      filters: getBoard(s.settingsByBoard, boardId).filters,
      setFilters: s.setFilters,
      resetFilters: s.resetFilters,
    })),
  );
  return {
    filters,
    setFilters: (f: TaskFilters) => setFilters(boardId, f),
    resetFilters: () => resetFilters(boardId),
  };
}

export function useSavedFilterSets(boardId: string) {
  const { savedFilterSets, setSets } = useTaskViewStore(
    useShallow((s) => ({
      savedFilterSets: getBoard(s.settingsByBoard, boardId).savedFilterSets,
      setSets: s.setSavedFilterSets,
    })),
  );
  return {
    savedFilterSets,
    setSavedFilterSets: (sets: SavedFilterSet[]) => setSets(boardId, sets),
  };
}

export function useTaskShowCompleted(boardId: string) {
  const { showCompleted, setShowCompleted } = useTaskViewStore(
    useShallow((s) => ({
      showCompleted: getBoard(s.settingsByBoard, boardId).showCompleted,
      setShowCompleted: s.setShowCompleted,
    })),
  );
  return {
    showCompleted,
    setShowCompleted: (show: boolean) => setShowCompleted(boardId, show),
  };
}

export function useTaskShowArchived(boardId: string) {
  const { showArchived, setShowArchived } = useTaskViewStore(
    useShallow((s) => ({
      showArchived: getBoard(s.settingsByBoard, boardId).showArchived,
      setShowArchived: s.setShowArchived,
    })),
  );
  return {
    showArchived,
    setShowArchived: (show: boolean) => setShowArchived(boardId, show),
  };
}

export function useLastActiveBoardView(boardId: string) {
  const { lastActiveView, setLastActiveView } = useTaskViewStore(
    useShallow((s) => ({
      lastActiveView: getBoard(s.settingsByBoard, boardId).lastActiveView,
      setLastActiveView: s.setLastActiveView,
    })),
  );
  return {
    lastActiveView,
    setLastActiveView: (view: BoardView) => setLastActiveView(boardId, view),
  };
}

/** List view's own *personal* display config (grouping/sort) — column visibility/order/width is shared, see `useSharedListViewConfig`. */
export function useTaskListConfig(boardId: string) {
  const { listConfig, setListConfig } = useTaskViewStore(
    useShallow((s) => ({
      listConfig: getBoard(s.settingsByBoard, boardId).views.list,
      setListConfig: s.setListConfig,
    })),
  );
  return {
    listConfig,
    setListConfig: (
      updater:
        | TaskListPersistedConfig
        | ((prev: TaskListPersistedConfig) => TaskListPersistedConfig),
    ) => setListConfig(boardId, updater),
  };
}

/** Timeline's "Lapso" (zoom) granularity, persisted per board like List's grouping. */
export function useTimelineZoom(boardId: string) {
  const { zoom, setTimelineZoom } = useTaskViewStore(
    useShallow((s) => ({
      zoom: getBoard(s.settingsByBoard, boardId).views.timeline.zoom,
      setTimelineZoom: s.setTimelineZoom,
    })),
  );
  return {
    zoom,
    setZoom: (next: TimelineZoom) => setTimelineZoom(boardId, next),
  };
}
